import { Badge, Popover } from "antd";
import { type FC } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import weekday from "dayjs/plugin/weekday";
import { useTranslation } from "react-i18next";
import { useCalendarEvents } from "@/features/calendar/model";
import { CalendarEventListDesktop } from "@/entities/calendar-event";
import { isVoid } from "@/shared/utils";
import { useAppSelector } from "@/services/store";
import type { Dayjs } from "dayjs";
import styles from "./styles.module.scss";

dayjs.extend(isToday);
dayjs.extend(weekday);

type CalendarEventMobileProps = {
  date: Dayjs;
};

const isWeekend = (lang: string, date: Dayjs): boolean => {
  switch (lang) {
    case "en": {
      return date.weekday() === 5 || date.weekday() === 6;
    }
    case "ru": {
      return date.weekday() === 5 || date.weekday() === 6;
    }
    default: {
      return date.weekday() === 5 || date.weekday() === 6;
    }
  }
};

export const CellRenderDashboard: FC<CalendarEventMobileProps> = ({ date }) => {
  const { i18n } = useTranslation();

  const { hasEvents, isFetching, currentDateEvents } = useCalendarEvents(date);

  const { selectedDate } = useAppSelector((state) => state.calendarSlice);

  const getContent = () => {
    if (!hasEvents) {
      return null;
    }

    return (
      <div className={styles["content-container"]}>
        {!isVoid(currentDateEvents.importantNotes) && (
          <CalendarEventListDesktop date={date} events={currentDateEvents.importantNotes} />
        )}
        {!isVoid(currentDateEvents.birthdays) && (
          <CalendarEventListDesktop date={date} events={currentDateEvents.birthdays} />
        )}
        {!isVoid(currentDateEvents.anniversaries) && (
          <CalendarEventListDesktop date={date} events={currentDateEvents.anniversaries} />
        )}
        {!isVoid(currentDateEvents.notes) && (
          <CalendarEventListDesktop date={date} events={currentDateEvents.notes} />
        )}
      </div>
    );
  };

  return (
    <div className={classNames(styles.cell)}>
      <Popover content={getContent()} trigger="hover">
        {hasEvents && (
          <div className={styles["has-events"]}>
            <Badge color="gold" />
          </div>
        )}
        <span
          className={classNames(styles.content, {
            [styles.today]: date.isToday() && !isWeekend(i18n.language, date),
            [styles.weekend]: isWeekend(i18n.language, date),
            [styles.active]:
              date.isSame(dayjs(selectedDate), "day") &&
              !isWeekend(i18n.language, date) &&
              !date.isToday(),
            [styles["today-active"]]:
              date.isToday() &&
              date.isSame(dayjs(selectedDate), "day") &&
              !isWeekend(i18n.language, date),
            [styles["weekend-today"]]: date.isToday() && isWeekend(i18n.language, date),
            [styles["weekend-active"]]:
              date.isSame(dayjs(selectedDate), "day") && isWeekend(i18n.language, date),
            [styles.loading]: isFetching,
          })}
        >
          {date.date()}
        </span>
      </Popover>
    </div>
  );
};
